import { CopyText } from "@/components/CopyText";
import { PlannerDrawingLabel } from "@/features/drawings/PlannerDrawingPill";
import { useActiveProject } from "@/features/project/ActiveProject";
import { currencyFormatter } from "@/utils/currencyFormatter";
import styled from "@emotion/styled";
import { type NormalizedDrawing } from "@repo/api";
import { Icon, Text, imageStrikethroughIcon } from "@repo/skapa";
import { T, getLanguageLocal, t, useTranslation } from "@repo/translation";
import {
  LabelField,
  TwoColumnSectionContainer,
  localizeDateAndTime,
  useSettings,
} from "@repo/ui";

const ImgPlaceholder = styled.div`
  width: 100px;
  display: flex;
  flex-direction: column;
`;

export const DrawingDetails = ({ drawing }: { drawing: NormalizedDrawing }) => {
  const { opportunity } = useActiveProject();
  const { dateFnsLocale } = useTranslation();
  const { market, language, general } = useSettings();
  return (
    <>
      {drawing.image ? (
        <img src={drawing.image} />
      ) : (
        <ImgPlaceholder>
          <Icon paths={imageStrikethroughIcon} />
          <Text>
            <T translationKey="prompt.no_image" />
          </Text>
        </ImgPlaceholder>
      )}
      <PlannerDrawingLabel drawingInfo={drawing} />
      {drawing.name && (
        <LabelField
          id="name"
          editable={false}
          label={t("label.name")}
          value={drawing.name}
        />
      )}
      <LabelField
        id="id"
        editable={false}
        label={t("label.id")}
        value={drawing.id}
      />
      {drawing.shortcode && (
        <LabelField
          id="shortcode"
          editable={false}
          label={t("label.shortcode")}
          value={
            <CopyText
              text={drawing.shortcode}
              buttonTitle={t("action.copy_shortcode")}
              successMessage={t("prompt.shortcode_has_been_copied")}
            />
          }
        />
      )}
      <TwoColumnSectionContainer>
        <LabelField
          id="type"
          editable={false}
          label={t("label.type")}
          value={drawing.type}
        />
        <LabelField
          id="price"
          editable={false}
          label={t("label.price")}
          value={currencyFormatter({
            value: drawing.value ?? 0,
            currency: drawing.currency ?? opportunity?.currency,
            locale: getLanguageLocal(language, market),
            showSign: true,
            disableDecimals: Boolean(general?.localization?.disable_decimals),
            fractions: 2,
          })}
        />
        <LabelField
          id="created"
          editable={false}
          label={t("label.created")}
          value={localizeDateAndTime(drawing.created ?? "", dateFnsLocale)}
        />
        <LabelField
          id="updated"
          editable={false}
          label={t("label.updated")}
          value={localizeDateAndTime(drawing.updated ?? "", dateFnsLocale)}
        />
      </TwoColumnSectionContainer>
    </>
  );
};
